import { supabase } from './supabase';
import { getCachedSession } from './sessionCache';
import type {
    Lead, Client, Appointment, Estimate, PricebookItem,
    ServiceTemplate, Project, Equipment,
    ServiceContract, ServiceVisit, ServiceRequest,
    TimeLog, TeamMember, Crew, PrivateTemplate
} from '../types';
import { fetchTeamMembersByWorkspace } from './workspaceUsers';
import { detectarIdioma, traducir } from '../i18n/idioma';

export const getWorkspaceId = () => {
    try {
        const stored = localStorage.getItem('2bros_active_workspace');
        return stored || 'default';
    } catch {
        return 'default';
    }
};

// AI is metered per workspace, so every AI call carries the active one: the edge
// function charges that workspace after checking you belong to it. The
// 'default' fallback is not a workspace — it's sent as `undefined` (dropped from
// the JSON body) and the server then uses the oldest of the caller's own.
export const getWorkspaceIdForAi = (): string | undefined => {
    const id = getWorkspaceId();
    return id === 'default' ? undefined : id;
};

// Write-path guard. Reads share getWorkspaceId() (which falls back to 'default'
// so read helpers keep working), but CREATE/mutation paths must NOT run against
// the 'default' fallback — otherwise a record is written into the wrong,
// invisible workspace when localStorage was evicted (iOS ITP / in-app webviews)
// before the real workspace resolved. Throws a friendly, user-facing message.
export const requireWorkspaceId = (): string => {
    const id = getWorkspaceId();
    if (id === 'default') {
        throw new Error('Workspace still loading — please try again in a moment.');
    }
    return id;
};

// NOTE: every CREATE path below sets `workspace_id: requireWorkspaceId()` (not
// getWorkspaceId()) so a record can never be written into the 'default' fallback
// workspace when localStorage was evicted before the real workspace resolved.
// Reads and update/delete .eq() filters keep using getWorkspaceId().

export function toCamel(obj: any): any {
    if (obj === null || typeof obj !== 'object' || Array.isArray(obj)) return obj;
    const newObj: any = {};
    for (const key in obj) {
        if (key === 'workspace_id') continue;
        const camelKey = key.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
        newObj[camelKey] = obj[key];
    }
    return newObj;
}

export function toSnake(obj: any): any {
    if (obj === null || typeof obj !== 'object' || Array.isArray(obj)) return obj;
    const newObj: any = {};
    for (const key in obj) {
        if (key === 'workspaceId') continue;
        const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
        newObj[snakeKey] = obj[key];
    }
    return newObj;
}

function mapDocs<T>(data: any[] | null): T[] {
    if (!data) return [];
    return data.map(toCamel) as T[];
}

/**
 * Un fallo de LECTURA no es una lista vacía.
 *
 * Los lectores de este archivo hacían `const { data } = await supabase...` sin
 * mirar `error` ni una vez, y `mapDocs(null)` de aquí arriba devuelve `[]`. Con
 * eso un 401, un 403 o un 500 salen de la función exactamente igual que un
 * taller sin clientes: React Query lo guarda como ÉXITO durante dos minutos, la
 * pantalla dice «no hay nada» y al registro de errores no llega ni una línea.
 * Por eso /finanzas, /seguimientos, /inventario, /clases y /prospectos no tienen
 * NI UN error en todo el historial de la tabla, mientras las migas de red de
 * otras incidencias del 4 y el 13 de julio de 2026 muestran esas mismas tablas
 * contestando 401 mientras la pantalla se pintaba vacía y callada.
 *
 * La regla, en la línea que sigue a cada lector de aquí abajo: atar `error`,
 * registrarlo y LANZAR. Lanzando, React Query reintenta y se queda con lo
 * último bueno en caché, en vez de sustituirlo por el vacío.
 *
 * `error.message` y no `error`: `utils/consolePatch.ts` serializa el objeto
 * entero a JSON, y los patrones de ruido de `utils/logger.ts` que buscan
 * `"code":"42501"` o `"code":"PGRST303"` se comerían el aviso justo en los
 * casos que hay que ver. Una cadena pelada no encaja con ninguno.
 *
 * Y ESO CUESTA: esos dos filtros existen porque gotrue se cura solo (renueva el
 * token y React Query reconsulta al volver el foco), así que saltárselos SUBE
 * el volumen del panel de errores. Un portátil que despierta con el token
 * vencido reconsulta a la vez todas las pantallas y deja una tanda de filas en
 * `system_error_logs`. Es a propósito —el silencio salía más caro—, pero si el
 * panel se vuelve ilegible lo que toca es deduplicar o limitar la frecuencia
 * AHÍ, no volver a registrar el objeto entero.
 *
 * Y el `wsId` viaja como PARÁMETRO OBLIGATORIO en vez de leerse aquí dentro con
 * `getWorkspaceId()`: ese respaldo devuelve la cadena 'default' cuando el
 * localStorage no está disponible, y consultar un taller que no existe da otra
 * lista vacía sin error. Obligatorio a propósito — así el compilador encuentra
 * los llamadores que se quedaron sin pasarlo.
 */

/**
 * `''` no es una fecha.
 *
 * Un `<input type="date">` vacío vale `''`, y Postgres contesta a eso
 * `invalid input syntax for type timestamp with time zone: ""`. Rechaza la fila
 * ENTERA, no el campo: por eso el síntoma que se reporta nunca es «no se guardó
 * la fecha» sino «no se guarda NADA». El caso que lo destapó fue no poder
 * corregir el NOMBRE de un equipo, y la fecha ni se había tocado — venía vacía
 * de fábrica porque la ficha la rellena con `(item.nextServiceDate || '')`.
 *
 * El criterio es el NOMBRE de la columna, no una lista de campos de texto como
 * la de `sanitizePayload`. Esa lista hay que mantenerla tabla por tabla, y el
 * día que alguien añade un campo de texto y se olvida de apuntarlo, su valor
 * vacío se guarda como NULL sin que nadie se entere. Aquí es estructural: una
 * columna `*_date`, `*_at`, `*_on` o `date` no puede recibir una cadena vacía,
 * y ninguna otra columna se toca.
 *
 * NULL y no `undefined`: en una EDICIÓN hay que poder BORRAR la fecha que ya
 * estaba. `undefined` desaparece al serializar el JSON y la columna se quedaría
 * con el valor viejo.
 */
const CLAVE_DE_FECHA = /(?:^|_)date$|_at$|_on$/;

export function sinFechasVacias<T extends Record<string, any>>(payload: T): T {
    const limpio: Record<string, any> = { ...payload };
    for (const clave of Object.keys(limpio)) {
        if (limpio[clave] === '' && CLAVE_DE_FECHA.test(clave)) limpio[clave] = null;
    }
    return limpio as T;
}

/**
 * Una escritura que no tocó NINGUNA fila no es un éxito.
 *
 * PostgREST contesta a un PATCH o un DELETE que no encaja con ninguna fila
 * exactamente igual que a uno que sí: 204 y `error: null`. Quien solo mira
 * `error` da por guardado lo que se acaba de perder. Así se fue un descuento de
 * $910 de un estimado en producción — el filtro de taller no encajaba, la
 * pantalla dijo «guardado», y al recargar volvió el importe viejo.
 *
 * La comprobación es pedir la fila de vuelta con `.select('id')` y contarla. Ya
 * estaba escrita a mano en `updateProjectDoc`, en `updateTimeLogDoc` y en
 * `services/office.ts`; esto es esa misma comprobación, una sola vez.
 *
 * OJO — dónde NO va:
 *  - En un lector con `.single()`: con cero filas ya lanza PGRST116 por su
 *    cuenta, y «no encontrado» al LEER es otro caso, no una escritura perdida.
 *  - Donde cero filas sea el resultado legítimo. `batchUpdateTimeLogs` es el
 *    ejemplo: solo toca el turno EN CURSO, y quien se renombra fuera de jornada
 *    no tiene ninguno que tocar.
 */
type RespuestaEscritura = { data: unknown[] | null; error: { message: string } | null };

async function exigirFilas(
    consulta: PromiseLike<RespuestaEscritura>,
    origen: string,
    fila: string,
    accion: 'updated' | 'deleted',
): Promise<void> {
    const { data, error } = await consulta;
    if (error) { console.error(`[${origen}] Error:`, error); throw new Error(error.message); }
    if (!data || data.length === 0) {
        const msg = `${fila} not ${accion} (0 rows) — workspace mismatch or RLS policy blocked the ${accion === 'deleted' ? 'delete' : 'write'}.`;
        console.error(`[${origen}]`, msg);
        throw new Error(msg);
    }
}

// ============================================================
//  LEADS
// ============================================================
export async function fetchLeads(wsId: string): Promise<Lead[]> {
    const { data, error } = await supabase.from('leads').select('*').eq('workspace_id', wsId);
    if (error) { console.error('[fetchLeads] error:', error.message); throw new Error(error.message); }
    return mapDocs(data);
}
export async function addLeadDoc(data: any) {
    const { data: inserted, error } = await supabase.from('leads').insert({ ...toSnake(data), workspace_id: requireWorkspaceId() }).select().single();
    if (error) { console.error('[addLeadDoc] Error:', error); throw new Error(error.message); }
    return inserted?.id || 'stub-id';
}
export async function updateLeadDoc(id: string, updates: Partial<Lead>) {
    await exigirFilas(
        supabase.from('leads').update(toSnake(updates)).eq('id', id).eq('workspace_id', getWorkspaceId()).select('id'),
        'updateLeadDoc', `Lead ${id}`, 'updated',
    );
}
export async function deleteLeadDoc(id: string) {
    const { error } = await supabase.from('leads').delete().eq('id', id).eq('workspace_id', getWorkspaceId());
    if (error) { console.error('[deleteLeadDoc] Error:', error); throw new Error(error.message); }
}

/** Cuánto vale una reserva de conversión antes de darla por abandonada. Una
 *  conversión sana tarda menos de un segundo; dos minutos solo los agota quien
 *  cerró el navegador a media faena. */
const CONVERSION_LEASE_MS = 2 * 60 * 1000;

/**
 * Reserva el derecho a convertir ESTE prospecto en cliente. Devuelve si la
 * reserva es tuya.
 *
 * Existe porque la conversión ya no la dispara solo una persona: cuando el
 * cliente firma la propuesta desde el enlace público, el barrido salta SOLO en
 * cada pestaña que tenga la app abierta. Dos pestañas convirtiendo a la vez
 * creaban DOS fichas del mismo cliente — el filtro de duplicados lee la caché de
 * su propia pestaña, que todavía no sabe del alta de la otra, y la tabla
 * `clients` no tiene índice único que lo atrape.
 *
 * El candado es la propia columna `won_at`, con un UPDATE condicional: Postgres
 * serializa las escrituras a la misma fila, así que de dos llamadas simultáneas
 * exactamente una toca una fila y el resto se va de vacío. Sin tabla nueva y sin
 * migración.
 *
 * Reservar NO cierra la etapa: el estado se cambia al FINAL, cuando el cliente
 * ya existe. Una reserva abandonada solo deja una marca de tiempo que caduca
 * sola, y el prospecto sigue movible mientras tanto.
 */
export async function claimLeadConversion(leadId: string): Promise<boolean> {
    const caducadaAntesDe = new Date(Date.now() - CONVERSION_LEASE_MS).toISOString();
    const { data, error } = await supabase
        .from('leads')
        .update({ won_at: new Date().toISOString() })
        .eq('id', leadId)
        .eq('workspace_id', getWorkspaceId())
        // Libre, o reservada hace tanto que quien la tomó ya no está. El filtro
        // por estado protege al prospecto YA ganado: ese no se re-reserva nunca,
        // porque su won_at es un hecho, no una reserva.
        .neq('status', 'Won')
        .or(`won_at.is.null,won_at.lt.${caducadaAntesDe}`)
        .select('id');
    if (error) { console.error('[claimLeadConversion] Error:', error); throw new Error(error.message); }
    return (data?.length ?? 0) > 0;
}

/** Suelta una reserva que no llegó a buen puerto, para que el siguiente intento
 *  no tenga que esperar a que caduque. Silencioso: si falla, la reserva caduca
 *  sola en dos minutos. */
export async function releaseLeadConversion(leadId: string): Promise<void> {
    const { error } = await supabase
        .from('leads')
        .update({ won_at: null })
        .eq('id', leadId)
        .eq('workspace_id', getWorkspaceId())
        .neq('status', 'Won');
    if (error) console.warn('[releaseLeadConversion] no se pudo soltar la reserva:', error.message);
}

/** Los clientes del taller, recién leídos de la base. Lo usa el perdedor de una
 *  reserva para encontrar la ficha que acaba de crear el ganador — su caché
 *  local todavía no la tiene. */
export async function fetchClientsFresh(): Promise<Client[]> {
    const { data, error } = await supabase
        .from('clients').select('*')
        .eq('workspace_id', getWorkspaceId());
    if (error) { console.error('[fetchClientsFresh] Error:', error.message); throw new Error(error.message); }
    return mapDocs(data);
}

/**
 * Traspasa al cliente recién creado todo lo que se hizo mientras era prospecto.
 *
 * Hoy eso son las propuestas con `source_lead_id = leadId` (las que se armaron
 * antes de que el cliente existiera) y, si alguna ya generó un proyecto, ese
 * proyecto — que se creó copiando el `client_id` vacío de su propuesta y por
 * tanto quedaría colgando de nadie.
 *
 * Poner `source_lead_id` en NULL es lo que hace la operación IDEMPOTENTE: es la
 * marca de "esta propuesta ya tiene dueño". Sin ella, volver a convertir el
 * mismo prospecto —o el barrido de utils/leadProposals— re-escribiría propuestas
 * que el contratista pudo haber reasignado a mano a otro cliente.
 *
 * @returns cuántas propuestas se traspasaron.
 */
export async function attachLeadWorkToClient(
    leadId: string,
    clientId: string,
    clientName: string,
): Promise<number> {
    const wsId = getWorkspaceId();

    const { data: rows, error } = await supabase
        .from('estimates')
        .update({ client_id: clientId, client_name: clientName, source_lead_id: null })
        .eq('source_lead_id', leadId)
        .eq('workspace_id', wsId)
        .select('id, converted_to_project_id');
    if (error) { console.error('[attachLeadWorkToClient] estimates:', error); throw new Error(error.message); }

    const projectIds = (rows || [])
        .map((r: any) => r.converted_to_project_id)
        .filter((id: string | null): id is string => !!id);

    if (projectIds.length > 0) {
        const { error: projErr } = await supabase
            .from('projects')
            .update({ client_id: clientId, client_name: clientName })
            .in('id', projectIds)
            .eq('workspace_id', wsId);
        // No se relanza: las propuestas —lo que importa— ya quedaron apuntadas.
        // Un proyecto sin cliente se nota y se arregla; perder el traspaso
        // entero por esto sería peor.
        if (projErr) console.error('[attachLeadWorkToClient] projects:', projErr);
    }

    return (rows || []).length;
}

// ============================================================
//  CLIENTS
// ============================================================
export async function fetchClients(wsId: string): Promise<Client[]> {
    const { data, error } = await supabase.from('clients').select('*').eq('workspace_id', wsId);
    if (error) { console.error('[fetchClients] error:', error.message); throw new Error(error.message); }
    return mapDocs(data);
}
export async function addClientDoc(data: any) {
    const { data: inserted, error } = await supabase.from('clients').insert({ ...toSnake(data), workspace_id: requireWorkspaceId() }).select().single();
    if (error) { console.error('[addClientDoc] Error:', error); throw new Error(error.message); }
    return inserted?.id || 'stub-id';
}
export async function updateClientDoc(id: string, updates: Partial<Client>) {
    await exigirFilas(
        supabase.from('clients').update(toSnake(updates)).eq('id', id).eq('workspace_id', getWorkspaceId()).select('id'),
        'updateClientDoc', `Client ${id}`, 'updated',
    );
}
export async function deleteClientDoc(id: string) {
    await exigirFilas(
        supabase.from('clients').delete().eq('id', id).eq('workspace_id', getWorkspaceId()).select('id'),
        'deleteClientDoc', `Client ${id}`, 'deleted',
    );
}

// ============================================================
//  APPOINTMENTS
// ============================================================
export async function fetchAppointments(wsId: string): Promise<Appointment[]> {
    const { data, error } = await supabase.from('appointments').select('*').eq('workspace_id', wsId);
    if (error) { console.error('[fetchAppointments] error:', error.message); throw new Error(error.message); }
    return mapDocs(data);
}
export async function addAppointmentDoc(data: any) {
    const { data: inserted, error } = await supabase.from('appointments').insert({ ...toSnake(data), workspace_id: requireWorkspaceId() }).select().single();
    if (error) { console.error('[addAppointmentDoc] Error:', error); throw new Error(error.message); }
    return inserted?.id || 'stub-id';
}
export async function deleteAppointmentDoc(id: string) {
    const { error } = await supabase.from('appointments').delete().eq('id', id).eq('workspace_id', getWorkspaceId());
    if (error) { console.error('[deleteAppointmentDoc] Error:', error); throw new Error(error.message); }
}

// ============================================================
//  ESTIMATES
// ============================================================

const estimateAllowedKeys = [
    'id', 'clientId', 'clientName', 'items', 'total', 'status',
    'dateCreated', 'lastUpdated', 'validUntil',
    'pricingSnapshot', 'adjustments', 'proposalSettings',
    'createdBy', 'createdByName',
    'signature', 'signedDate',
    'siteConditions', 'zones',
    'viewedAt', 'followUp1SentAt', 'followUp2SentAt', 'followUpsPaused',
    'automatedFollowUpsSent', 'lastAutomatedFollowUpAt', 'convertedToProjectId',
    // F6: per-phase counters. The single lifetime counter could not enforce a
    // per-phase maxEmails cap.
    'followUpsSentUnread', 'followUpsSentViewed',
    // Phase 1 (D-2): trade/scope context + contract-signing audit + warranty + decline
    // reason. These are type-defined on Estimate but were silently dropped on write
    // until both these whitelist entries AND the matching columns existed.
    'tradeId', 'scopeCategoryId', 'scopeLabel',
    'signedContractId', 'contractSignedFields',
    'selectedWarrantyId', 'warrantyText',
    'declineReason',
    // Phase 3 (L-1): terminal-state timestamps.
    'archivedAt', 'paidAt',
    // Phase 7 (F-2): real send timestamp, distinct from lastUpdated.
    'sentAt',
    // Propuestas hechas para un prospecto (todavía sin cliente).
    'sourceLeadId'
];

function filterEstimate(data: any) {
    const filtered: any = {};
    for (const key of estimateAllowedKeys) {
        if (data[key] !== undefined) filtered[key] = data[key];
    }
    return filtered;
}

// Accept an optional workspaceId so React Query callers can pass the value
// from the queryKey directly. Falls back to localStorage for legacy callers,
// but the explicit param avoids a race on first sign-in where the persist
// useEffect hasn't yet written the active workspace to localStorage.
export async function fetchEstimates(workspaceId?: string): Promise<Estimate[]> {
    const wsId = workspaceId ?? getWorkspaceId();
    const { data, error } = await supabase.from('estimates').select('*').eq('workspace_id', wsId);
    // Throw so React Query retries and keeps last-good cached data — returning
    // mapDocs(null) here would cache an EMPTY list as a *successful* fetch on a
    // transient network failure.
    if (error) { console.error('fetchEstimates error:', error.message); throw new Error(error.message); }
    return mapDocs(data);
}
export async function addEstimateDoc(id: string, data: any) {
    const { error } = await supabase.from('estimates').insert({ ...toSnake(filterEstimate(data)), id, workspace_id: requireWorkspaceId() });
    if (error) { console.error('[addEstimateDoc] Error:', error); throw new Error(error.message); }
}
/**
 * `requireWorkspaceId()` y no `getWorkspaceId()`: esto ESCRIBE dinero. Con el
 * localStorage desalojado (ITP de iOS, webviews de Instagram o Facebook) el
 * respaldo 'default' hacía que el PATCH saliera con `workspace_id=eq.default`,
 * no encajara con nada, y PostgREST contestara 204 sin error. Ese es el camino
 * exacto por el que se perdió un descuento de $910 sin que nadie viera un aviso.
 * Ahora falla antes de salir, con un mensaje que se puede leer.
 */
export async function updateEstimateDoc(id: string, updates: Partial<Estimate>) {
    const wsId = requireWorkspaceId();   // antes de montar nada: si el taller no está, no se escribe
    await exigirFilas(
        supabase.from('estimates').update(toSnake(filterEstimate(updates))).eq('id', id).eq('workspace_id', wsId).select('id'),
        'updateEstimateDoc', `Estimate ${id}`, 'updated',
    );
}
export async function deleteEstimateDoc(id: string) {
    const { error } = await supabase.from('estimates').delete().eq('id', id).eq('workspace_id', getWorkspaceId());
    if (error) { console.error('[deleteEstimateDoc] Error:', error); throw new Error(error.message); }
}
/**
 * Un estimado, SIEMPRE acotado al taller activo.
 *
 * ── Por qué ya no hay rama anónima ──────────────────────────────────────────
 *
 * Aquí había un atajo para la propuesta pública: si no había sesión, se leía
 * `estimates` sin filtrar y se dejaba que el RLS fuera el único guardia. El
 * problema es que la política que lo permitía era `anon_select_estimates
 * USING(true)`, o sea, ningún guardia: comprobado el 11-ago-2026 contra la API
 * de producción, con solo la llave publicable —la que va dentro del JavaScript
 * y cualquiera lee— y sin iniciar sesión se descargaban **371 estimados de 32
 * talleres distintos**, con nombre y dirección de sus clientes, importes,
 * `pricing_snapshot` (costos y márgenes), firmas ya recogidas, y el
 * `proposal_view_token` de cada uno.
 *
 * Ese token es la credencial de la página pública. Filtrado en masa, permitía
 * llamar a `respond_to_proposal` sobre propuestas ajenas.
 *
 * El camino anónimo legítimo es `getProposalByToken(token)` (RPC SECURITY
 * DEFINER), que entrega una versión redactada y solo de la propuesta cuyo
 * token se presenta. Se comprobó antes de cerrar esto: **371 de 371** estimados
 * tienen `proposal_view_token`, así que no queda ningún enlace vivo que
 * dependiera del atajo.
 */
export async function getEstimateDoc(id: string) {
    const wsId = getWorkspaceId();
    const { data, error } = await supabase.from('estimates').select('*').eq('id', id).eq('workspace_id', wsId).maybeSingle();
    // `maybeSingle` ya distingue «no está» (data null, error null) de «no se
    // pudo leer», así que aquí lanzar no convierte un no-encontrado en avería.
    if (error) { console.error('[getEstimateDoc] error:', error.message); throw new Error(error.message); }
    if (!data) return null;
    const result = toCamel(data);
    result.workspaceId = data.workspace_id;
    return result;
}

// ------------------------------------------------------------
//  PUBLIC PROPOSAL RPCS
// ------------------------------------------------------------

/** P1.1: token-scoped proposal read. Returns the single estimate matching the
 *  unguessable proposal_view_token + that estimate's own workspace settings, via
 *  a SECURITY DEFINER RPC — no blanket anon SELECT on estimates/settings needed.
 *  Returns null if the token doesn't match. Used by the public proposal viewer
 *  when a `?t=<token>` is present in the URL; the viewer falls back to the legacy
 *  anon-SELECT path when no token is present (backward compat during transition). */
export async function getProposalByToken(token: string): Promise<{ estimate: any; settings: any } | null> {
    if (!token) return null;
    const { data, error } = await supabase.rpc('get_proposal_by_token', { p_token: token });
    if (error) { console.error('[getProposalByToken] Error:', error); return null; }
    const row = Array.isArray(data) ? data[0] : data;
    if (!row || !row.estimate) return null;
    const estimate = toCamel(row.estimate);
    estimate.workspaceId = row.estimate.workspace_id;
    const settings = row.settings ? toCamel(row.settings) : null;
    return { estimate, settings };
}

/** C-2: redacted public proposal read. Unlike getProposalByToken (which returns the
 *  full unredacted estimate + settings row), this calls get_public_proposal which
 *  strips internal fields server-side (pricing_snapshot/cost-markup, proposal_view_token,
 *  workspace_id, created_by, follow-up cols) while KEEPING adjustments (the public
 *  pricing view needs tax/discount/deposit). `business` is a camelCase workspace subset
 *  (profile, contracts, defaultTerms, zelle*, socialLinks, reviewLinks). Preferred path. */
export async function getPublicProposal(token: string): Promise<{ estimate: any; business: any; expired: boolean; isOwner: boolean } | null> {
    if (!token) return null;
    const { data, error } = await supabase.rpc('get_public_proposal', { p_token: token });
    if (error) { console.error('[getPublicProposal] Error:', error); return null; }
    const row = Array.isArray(data) ? data[0] : data;
    if (!row || !row.estimate) return null;
    // estimate is snake_cased (to_jsonb minus stripped) → toCamel. business is already
    // camelCase (the RPC built it with camelCase keys), so pass it through as-is.
    // is_owner is computed server-side: true when the caller is a member of the
    // estimate's workspace (the contractor previewing their own proposal). Drives
    // the read-only preview that blocks the owner from signing their own proposal.
    const estimate = toCamel(row.estimate);
    return { estimate, business: row.business || null, expired: !!row.expired, isOwner: !!row.is_owner };
}

export async function trackProposalView(estimateId: string, token?: string | null): Promise<void> {
    // D-7: prefer the token overload (the only anon path kept after the SEC-4 lockdown).
    // Fall back to the legacy uuid overload during the soak for links without ?t=.
    const { error } = token
        ? await supabase.rpc('track_proposal_view', { p_token: token })
        : await supabase.rpc('track_proposal_view', { p_estimate_id: estimateId });
    if (error) {
        console.error('[trackProposalView] Error:', error);
        throw new Error(error.message);
    }
}

export async function respondToProposal(
    estimateId: string,
    status: string,
    signature?: string | null,
    projectData?: any | null,
    token?: string | null
): Promise<{ success: boolean; projectId?: string }> {
    // p_project_data is snake_cased here; the RPC reads signed_contract_id /
    // contract_signed_fields / decline_reason from it (C-3/F-6) and derives the
    // project itself (it ignores any client-built tasks/material_status now).
    const payload = projectData ? toSnake(projectData) : null;
    // D-7: prefer the token overload; uuid fallback during the soak.
    const { data, error } = token
        ? await supabase.rpc('respond_to_proposal', {
            p_token: token, p_status: status, p_signature: signature || null, p_project_data: payload,
          })
        : await supabase.rpc('respond_to_proposal', {
            p_estimate_id: estimateId, p_status: status, p_signature: signature || null, p_project_data: payload,
          });

    if (error) {
        // P0001 = deliberate RAISE from our respond_to_proposal RPC (expired /
        // not found / already signed) — expected validation, not a production
        // error, so don't ship it to the error dashboard. Attach the code so
        // the public proposal view can surface the RPC's own message.
        if (error.code !== 'P0001') {
            console.error('[respondToProposal] Error:', error);
        }
        const err = new Error(error.message);
        (err as any).code = error.code;
        throw err;
    }

    return toCamel(data);
}

/**
 * Create the project for an accepted estimate, server-side.
 *
 * Wraps the SAME engine the public signing flow uses (create_project_from_estimate),
 * so an in-app accept and a client signature produce an identical project —
 * same name, same task grouping, same material rows, same 'Accepted' status.
 *
 * Idempotent: if the estimate already has a project the RPC returns that id with
 * alreadyExisted=true instead of creating a second one, so this is safe to call
 * on an estimate whose conversion state is unknown.
 */
export async function convertEstimateToProjectRpc(
    estimateId: string,
): Promise<{ success: boolean; projectId?: string; alreadyExisted?: boolean }> {
    const { data, error } = await supabase.rpc('convert_estimate_to_project', {
        p_estimate_id: estimateId,
    });
    if (error) {
        console.error('[convertEstimateToProject] Error:', error);
        const err = new Error(error.message);
        (err as any).code = error.code;
        throw err;
    }
    return toCamel(data);
}

/** Mint a per-proposal access token. Called from the workspace-side flow when
 *  an estimate is sent to a client; the returned token gets embedded in the
 *  public proposal URL as `?t=<token>`. Workspace members only. */
export async function generateProposalToken(estimateId: string): Promise<string> {
    const { data, error } = await supabase.rpc('gen_proposal_token', { p_estimate_id: estimateId });
    if (error) {
        console.error('[generateProposalToken] Error:', error);
        throw new Error(error.message);
    }
    return data as string;
}

// ============================================================
//  PRICEBOOK
// ============================================================
export async function fetchPricebook(wsId: string): Promise<PricebookItem[]> {
    const { data, error } = await supabase.from('pricebook').select('*').eq('workspace_id', wsId);
    if (error) { console.error('[fetchPricebook] error:', error.message); throw new Error(error.message); }
    return mapDocs(data);
}
export async function addPricebookItemDoc(data: any) {
    const { data: inserted, error } = await supabase.from('pricebook').insert({ ...toSnake(data), workspace_id: requireWorkspaceId() }).select().single();
    if (error) { console.error('[addPricebookItemDoc] Error:', error); throw new Error(error.message); }
    return inserted?.id || 'stub-id';
}
export async function updatePricebookItemDoc(id: string, updates: Partial<PricebookItem>) {
    await exigirFilas(
        supabase.from('pricebook').update(toSnake(updates)).eq('id', id).eq('workspace_id', getWorkspaceId()).select('id'),
        'updatePricebookItemDoc', `Pricebook item ${id}`, 'updated',
    );
}
export async function deletePricebookItemDoc(id: string) {
    const { error } = await supabase.from('pricebook').delete().eq('id', id).eq('workspace_id', getWorkspaceId());
    if (error) { console.error('[deletePricebookItemDoc] Error:', error); throw new Error(error.message); }
}

// ============================================================
//  PRICEBOOK ↔ CATALOG_ITEMS INTEGRATION
//  Connects the global catalog_items library with per-workspace
//  pricebook entries. The proposal editor reads workspace prices
//  via fetchPricebookByCatalogItemIds and saves edits via
//  upsertWorkspaceCatalogPrice. Edit once, applies to every template
//  that references the same catalog_item_id.
// ============================================================

export interface WorkspacePriceEntry {
    catalogItemId: string;
    cost: number;
    markupPercent: number | null;
    vendor: string | null;
    source: 'user' | 'template';
    dedupStatus: 'pending' | 'reviewed_same' | 'reviewed_different';
    name: string;
    unit: string;
}

/** Bulk-fetch the workspace's saved prices for a set of catalog item ids.
 *  Returns a Map keyed by catalog_item_id. Hints not in the workspace's
 *  pricebook are absent from the map (callers should default to 0). */
export async function fetchPricebookByCatalogItemIds(
    catalogItemIds: string[]
): Promise<Map<string, WorkspacePriceEntry>> {
    const out = new Map<string, WorkspacePriceEntry>();
    if (catalogItemIds.length === 0) return out;
    const { data, error } = await supabase
        .from('pricebook')
        .select('catalog_item_id, cost, markup_percent, vendor, source, dedup_status, name, unit')
        .eq('workspace_id', getWorkspaceId())
        .in('catalog_item_id', catalogItemIds);
    if (error) {
        console.error('[fetchPricebookByCatalogItemIds] Error:', error);
        return out;
    }
    for (const row of (data || [])) {
        if (!row.catalog_item_id) continue;
        out.set(row.catalog_item_id, {
            catalogItemId: row.catalog_item_id,
            cost: Number(row.cost) || 0,
            markupPercent: row.markup_percent === null ? null : Number(row.markup_percent),
            vendor: row.vendor ?? null,
            source: (row.source ?? 'user') as 'user' | 'template',
            dedupStatus: (row.dedup_status ?? 'pending') as WorkspacePriceEntry['dedupStatus'],
            name: row.name ?? '',
            unit: row.unit ?? '',
        });
    }
    return out;
}

/** Upsert a single price for a catalog item in this workspace's pricebook.
 *  Used by the proposal editor's debounced save and the Pricebook view's
 *  inline edits.
 *
 *  - INSERT path: dedup_status arranca en 'pending' por defecto de columna.
 *  - UPDATE path: cost/markup se sobrescriben. `source` es lo que mande quien
 *    llama — el upsert de PostgREST compila a `ON CONFLICT DO UPDATE SET` sobre
 *    TODAS las columnas del payload, así que no existe una columna "solo al
 *    insertar". El docblock decía justo lo contrario y el código escribía
 *    'template' siempre: cada edición manual de precio se marcaba como si la
 *    hubiera puesto una plantilla, y desaparecía de "Mis artículos".
 *  - `dedup_status` se queda FUERA del payload a propósito: por defecto entra
 *    'pending' al insertar, y un UPDATE no debe reencolar una fila que alguien
 *    ya resolvió.
 *
 *  `seed` carries denormalized fields needed when inserting (name/category/
 *  unit) — sourced from the catalog_items row at the call site.
 */
export async function upsertWorkspaceCatalogPrice(
    catalogItemId: string,
    cost: number,
    seed: { name: string; category: string; unit: string },
    markupPercent: number | null = null,
    /** Procedencia. Hoy los cuatro sitios que llaman son ediciones de una
     *  persona —precio tecleado en el pricebook, en el catálogo, importado de
     *  una factura, o guardado desde un presupuesto que el usuario ya
     *  valoró— así que todos toman 'user'. El parámetro existe para cuando
     *  haya un sembrado automático de verdad, que sí sería 'template'. */
    source: 'user' | 'template' = 'user',
): Promise<void> {
    const workspaceId = getWorkspaceId();
    // Upsert keyed on (workspace_id, catalog_item_id) — the unique partial
    // index from migration 018 makes this safe.
    const payload: Record<string, any> = {
        workspace_id: workspaceId,
        catalog_item_id: catalogItemId,
        cost,
        markup_percent: markupPercent,
        /* La PROCEDENCIA del número, no solo el número (plan de precios v2,
         * decisión 4). `null` significa «nadie tecleó nada, sigue el dial», así
         * que su procedencia es 'dial'; un número explícito es una decisión de
         * una persona y se sella 'user' para que gane sobre el dial.
         *
         * Sin esto la columna nacía en 'dial' para todo el mundo, incluido un
         * 60% que alguien hubiera tecleado a mano — y el día que el lector
         * empezara a exigir 'user', esa decisión se perdería en silencio. */
        markup_source: markupPercent === null || markupPercent === undefined ? 'dial' : 'user',
        name: seed.name,
        category: seed.category,
        unit: seed.unit,
        source,
        last_updated: new Date().toISOString(),
    };
    const { error } = await supabase
        .from('pricebook')
        .upsert(payload, { onConflict: 'workspace_id,catalog_item_id', ignoreDuplicates: false });
    if (error) {
        console.error('[upsertWorkspaceCatalogPrice] Error:', error);
        throw new Error(error.message);
    }
}

/** All catalog items LEFT JOIN'd with this workspace's pricebook prices.
 *  Used by the Pricebook view to render every catalog row with the
 *  workspace's price filled in (or null if not yet priced).
 *  Single round trip — pricebook fetched once, joined client-side against
 *  the bundled catalog snapshot. */
export async function fetchWorkspacePricebookOverrides(): Promise<Map<string, WorkspacePriceEntry>> {
    const out = new Map<string, WorkspacePriceEntry>();
    const { data, error } = await supabase
        .from('pricebook')
        .select('catalog_item_id, cost, markup_percent, vendor, source, dedup_status, name, unit')
        .eq('workspace_id', getWorkspaceId())
        .not('catalog_item_id', 'is', null);
    if (error) {
        console.error('[fetchWorkspacePricebookOverrides] Error:', error);
        return out;
    }
    for (const row of (data || [])) {
        if (!row.catalog_item_id) continue;
        out.set(row.catalog_item_id, {
            catalogItemId: row.catalog_item_id,
            cost: Number(row.cost) || 0,
            markupPercent: row.markup_percent === null ? null : Number(row.markup_percent),
            vendor: row.vendor ?? null,
            source: (row.source ?? 'user') as 'user' | 'template',
            dedupStatus: (row.dedup_status ?? 'pending') as WorkspacePriceEntry['dedupStatus'],
            name: row.name ?? '',
            unit: row.unit ?? '',
        });
    }
    return out;
}

// ============================================================
//  SERVICE TEMPLATES
// ============================================================
export async function fetchTemplates(wsId: string): Promise<ServiceTemplate[]> {
    const { data, error } = await supabase.from('service_templates').select('*').eq('workspace_id', wsId);
    if (error) { console.error('[fetchTemplates] error:', error.message); throw new Error(error.message); }
    return mapDocs(data);
}
export async function addTemplateDoc(data: any) {
    const { data: inserted, error } = await supabase.from('service_templates').insert({ ...toSnake(data), workspace_id: requireWorkspaceId() }).select().single();
    if (error) { console.error('[addTemplateDoc] Error:', error); throw new Error(error.message); }
    return inserted?.id || 'stub-id';
}
export async function updateTemplateDoc(id: string, updates: Partial<ServiceTemplate>) {
    await exigirFilas(
        supabase.from('service_templates').update(toSnake(updates)).eq('id', id).eq('workspace_id', getWorkspaceId()).select('id'),
        'updateTemplateDoc', `Service template ${id}`, 'updated',
    );
}

// ============================================================
//  PRIVATE TEMPLATES (workspace-scoped, derived from global templates)
// ============================================================

export async function fetchPrivateTemplates(workspaceId?: string): Promise<PrivateTemplate[]> {
    const wsId = workspaceId ?? getWorkspaceId();
    const { data, error } = await supabase
        .from('private_templates')
        .select('*')
        .eq('workspace_id', wsId)
        .order('updated_at', { ascending: false });
    // Throw so React Query retries and keeps last-good cached data instead of
    // caching an empty list as success (see fetchEstimates).
    if (error) { console.error('[fetchPrivateTemplates] Error:', error.message); throw new Error(error.message); }
    return mapDocs<PrivateTemplate>(data);
}

/** Insert a new workspace-private template. Caller passes the contractor's
 *  chosen name + the line items snapshot at save time. */
export async function addPrivateTemplate(
    name: string,
    items: any[],
    sourceTemplateId?: string | null,
): Promise<PrivateTemplate | null> {
    const workspaceId = getWorkspaceId();
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
        .from('private_templates')
        .insert({
            workspace_id: workspaceId,
            name,
            source_template_id: sourceTemplateId ?? null,
            items,
            created_by: user?.id ?? null,
        })
        .select()
        .single();
    if (error) { console.error('[addPrivateTemplate] Error:', error); throw new Error(error.message); }
    if (!data) return null;
    const result = toCamel(data) as PrivateTemplate;
    result.workspaceId = data.workspace_id;
    return result;
}

export async function updatePrivateTemplate(id: string, updates: Partial<PrivateTemplate>): Promise<void> {
    const { error } = await supabase
        .from('private_templates')
        .update(toSnake(updates))
        .eq('id', id)
        .eq('workspace_id', getWorkspaceId());
    if (error) { console.error('[updatePrivateTemplate] Error:', error); throw new Error(error.message); }
}

export async function deletePrivateTemplate(id: string): Promise<void> {
    const { error } = await supabase
        .from('private_templates')
        .delete()
        .eq('id', id)
        .eq('workspace_id', getWorkspaceId());
    if (error) { console.error('[deletePrivateTemplate] Error:', error); throw new Error(error.message); }
}

// ============================================================
//  PROJECTS
// ============================================================
export async function fetchProjects(wsId: string): Promise<Project[]> {
    const { data, error } = await supabase.from('projects').select('*').eq('workspace_id', wsId);
    // Throw so React Query retries and keeps last-good cached data instead of
    // caching an empty list as success (see fetchEstimates).
    if (error) { console.error('fetchProjects error:', error.message); throw new Error(error.message); }
    return mapDocs(data);
}
export async function addProjectDoc(data: any) {
    const { data: inserted, error } = await supabase.from('projects').insert({ ...toSnake(data), workspace_id: requireWorkspaceId() }).select().single();
    if (error) { console.error('[addProjectDoc] Error:', error); throw new Error(error.message); }
    return inserted?.id || 'stub-id';
}
export async function updateProjectDoc(id: string, updates: Partial<Project>) {
    // .select() lets us detect a silent 0-row update (RLS denial or workspace
    // mismatch) — without it, Supabase returns no error and the change is lost.
    const { data, error } = await supabase
        .from('projects')
        .update(toSnake(updates))
        .eq('id', id)
        .eq('workspace_id', getWorkspaceId())
        .select('id');
    if (error) { console.error('[updateProjectDoc] Error:', error); throw new Error(error.message); }
    if (!data || data.length === 0) {
        const msg = `Project ${id} not updated (0 rows) — workspace mismatch or RLS policy blocked the write.`;
        console.error('[updateProjectDoc]', msg);
        throw new Error(msg);
    }
}
export async function transitionProjectStatusDoc(id: string, status: string) {
    // Atomic lifecycle transition via SECURITY DEFINER RPC: stamps the matching
    // timestamp and, on Completed, sets completion_percent=100 + end_date.
    const { error } = await supabase.rpc('project_transition_status', { p_id: id, p_to: status });
    if (error) { console.error('[transitionProjectStatusDoc] Error:', error); throw new Error(error.message); }
}
export async function appendProjectNoteDoc(id: string, note: any) {
    // Atomic server-side append (avoids the lost-update of rewriting the whole notes[]).
    // Notes are stored camelCase — toSnake/toCamel are shallow, so pass the note as-is.
    const { error } = await supabase.rpc('append_project_note', { p_id: id, p_note: note });
    if (error) { console.error('[appendProjectNoteDoc] Error:', error); throw new Error(error.message); }
}
// Element-level task ops (P3): atomic + server-side completion_percent recompute (FOR
// UPDATE row lock), so concurrent task edits don't clobber the whole tasks[] array.
export async function appendProjectTaskDoc(id: string, task: any) {
    const { error } = await supabase.rpc('append_project_task', { p_id: id, p_task: task });
    if (error) { console.error('[appendProjectTaskDoc] Error:', error); throw new Error(error.message); }
}
export async function patchProjectTaskDoc(id: string, taskId: string, patch: any) {
    const { error } = await supabase.rpc('patch_project_task', { p_id: id, p_task_id: taskId, p_patch: patch });
    if (error) { console.error('[patchProjectTaskDoc] Error:', error); throw new Error(error.message); }
}
export async function removeProjectTaskDoc(id: string, taskId: string) {
    const { error } = await supabase.rpc('remove_project_task', { p_id: id, p_task_id: taskId });
    if (error) { console.error('[removeProjectTaskDoc] Error:', error); throw new Error(error.message); }
}
export async function deleteProjectDoc(id: string) {
    // P10: cascade via RPC — frees the job's equipment + unlinks its appointments, then
    // deletes the row (no more dangling project_id references).
    const { error } = await supabase.rpc('delete_project_cascade', { p_id: id });
    if (error) { console.error('[deleteProjectDoc] Error:', error); throw new Error(error.message); }
}

// ============================================================
//  EQUIPMENT
// ============================================================
export async function fetchEquipment(wsId: string): Promise<Equipment[]> {
    const { data, error } = await supabase.from('equipment').select('*').eq('workspace_id', wsId);
    if (error) { console.error('[fetchEquipment] error:', error.message); throw new Error(error.message); }
    return mapDocs(data);
}
export async function addEquipmentDoc(data: any) {
    const { data: inserted, error } = await supabase.from('equipment').insert({ ...sinFechasVacias(toSnake(data)), workspace_id: requireWorkspaceId() }).select().single();
    if (error) { console.error('[addEquipmentDoc] Error:', error); throw new Error(error.message); }
    return inserted?.id || 'stub-id';
}
export async function updateEquipmentDoc(id: string, updates: Partial<Equipment>) {
    await exigirFilas(
        supabase.from('equipment').update(sinFechasVacias(toSnake(updates))).eq('id', id).eq('workspace_id', getWorkspaceId()).select('id'),
        'updateEquipmentDoc', `Equipment ${id}`, 'updated',
    );
}
export async function deleteEquipmentDoc(id: string) {
    // .select('id') lets us detect a silent 0-row delete (RLS denial / workspace
    // mismatch) — without it, Supabase returns no error and the UI "succeeds".
    const { data, error } = await supabase.from('equipment').delete().eq('id', id).eq('workspace_id', getWorkspaceId()).select('id');
    if (error) { console.error('[deleteEquipmentDoc] Error:', error); throw new Error(error.message); }
    if (!data || data.length === 0) {
        throw new Error(`Equipment ${id} not deleted (0 rows) — workspace mismatch or RLS policy blocked the delete.`);
    }
}
export async function addEquipmentLogDoc(equipId: string, data: any) {
    const { data: inserted, error } = await supabase.from('maintenance_logs').insert({ ...toSnake({ ...data, equipmentId: equipId }), workspace_id: requireWorkspaceId() }).select().single();
    if (error) { console.error('[addEquipmentLogDoc] Error:', error); throw new Error(error.message); }
    return inserted?.id || 'stub-id';
}
export async function fetchEquipmentLogs(wsId: string, equipId: string): Promise<any[]> {
    const { data, error } = await supabase.from('maintenance_logs').select('*').eq('equipment_id', equipId).eq('workspace_id', wsId);
    if (error) { console.error('[fetchEquipmentLogs] error:', error.message); throw new Error(error.message); }
    return mapDocs(data);
}

// ============================================================
//  MAINTENANCE LOGS (Fleet)
// ============================================================
export async function fetchMaintenanceLogs(wsId: string): Promise<any[]> {
    const { data, error } = await supabase.from('maintenance_logs').select('*').eq('workspace_id', wsId);
    if (error) { console.error('[fetchMaintenanceLogs] error:', error.message); throw new Error(error.message); }
    return mapDocs(data);
}
export async function addMaintenanceLogDoc(data: any) {
    const { data: inserted, error } = await supabase.from('maintenance_logs').insert({ ...toSnake(data), workspace_id: requireWorkspaceId() }).select().single();
    if (error) { console.error('[addMaintenanceLogDoc] Error:', error); throw new Error(error.message); }
    return inserted?.id || 'stub-id';
}

// ============================================================
//  SERVICES (Contracts & Visits)
// ============================================================
export async function fetchServiceContracts(wsId: string): Promise<ServiceContract[]> {
    const { data, error } = await supabase.from('service_contracts').select('*').eq('workspace_id', wsId);
    if (error) { console.error('[fetchServiceContracts] error:', error.message); throw new Error(error.message); }
    return mapDocs(data);
}

export async function fetchServiceVisits(wsId: string): Promise<ServiceVisit[]> {
    const { data, error } = await supabase.from('service_visits').select('*').eq('workspace_id', wsId).order('sequence_index', { ascending: true });
    if (error) { console.error('[fetchServiceVisits] error:', error.message); throw new Error(error.message); }
    return mapDocs(data);
}

/** Sanitize empty strings to null for timestamp/date columns */
function sanitizePayload(payload: any): any {
    const textFields = new Set(['description', 'notes', 'service_address', 'client_name', 'preferred_day_of_week', 'crew_notes', 'status', 'billing_type', 'frequency']);
    const cleaned = { ...payload };
    for (const key of Object.keys(cleaned)) {
        // Convert empty strings to null for non-text fields (timestamps, numbers, etc.)
        if (cleaned[key] === '' && !textFields.has(key)) {
            cleaned[key] = null;
        }
    }
    return cleaned;
}

export async function addServiceContractDoc(data: any) {
    const payload = sanitizePayload({ ...toSnake(data), workspace_id: requireWorkspaceId() });
    console.log('[addServiceContractDoc] Inserting:', payload);
    const { data: inserted, error } = await supabase.from('service_contracts').insert(payload).select().single();
    if (error) {
        console.error('[addServiceContractDoc] Error:', error);
        throw new Error(error.message);
    }
    return inserted?.id || 'stub-id';
}
export async function updateServiceContractDoc(id: string, updates: Partial<ServiceContract>) {
    await exigirFilas(
        supabase.from('service_contracts').update(sanitizePayload(toSnake(updates))).eq('id', id).eq('workspace_id', getWorkspaceId()).select('id'),
        'updateServiceContractDoc', `Service contract ${id}`, 'updated',
    );
}
export async function deleteServiceContractDoc(id: string) {
    const { data, error } = await supabase.from('service_contracts').delete().eq('id', id).eq('workspace_id', getWorkspaceId()).select('id');
    if (error) {
        console.error('[deleteServiceContractDoc] Error:', error);
        throw new Error(error.message);
    }
    if (!data || data.length === 0) {
        throw new Error(`Service contract ${id} not deleted (0 rows) — workspace mismatch or RLS policy blocked the delete.`);
    }
}
// ============================================================
//  CONTRACT INVOICES (monthly billing for service routes)
// ============================================================
export async function fetchContractInvoices(wsId: string): Promise<any[]> {
    const { data, error } = await supabase.from('contract_invoices')
        .select('*')
        .eq('workspace_id', wsId)
        .order('period', { ascending: false });
    // Throw rather than swallow: an empty billing tab caused by a failed read
    // looks identical to "nothing billed yet", and the office would re-generate
    // invoices that already exist.
    if (error) { console.error('[fetchContractInvoices] Error:', error.message); throw new Error(error.message); }
    return mapDocs(data);
}

export async function addContractInvoiceDoc(data: any) {
    const { data: inserted, error } = await supabase.from('contract_invoices')
        .insert({ ...toSnake(data), workspace_id: requireWorkspaceId() })
        .select().single();
    if (error) { console.error('[addContractInvoiceDoc] Error:', error); throw new Error(error.message); }
    return inserted?.id || 'stub-id';
}

/** Insert many at once — one round trip for a whole month's generation.
 *  `ignoreDuplicates` leans on the UNIQUE (contract_id, period) constraint so a
 *  second press of Generate is a no-op instead of an error the user has to read. */
export async function addContractInvoiceDocs(rows: any[]) {
    if (rows.length === 0) return 0;
    const wsId = requireWorkspaceId();
    const payload = rows.map(r => ({ ...toSnake(r), workspace_id: wsId }));
    const { data, error } = await supabase.from('contract_invoices')
        .upsert(payload, { onConflict: 'contract_id,period', ignoreDuplicates: true })
        .select();
    if (error) { console.error('[addContractInvoiceDocs] Error:', error); throw new Error(error.message); }
    return data?.length ?? 0;
}

/**
 * Esto es un libro de facturas: marcar una como COBRADA y que no se guarde nada
 * es la misma clase de pérdida silenciosa que la del estimado. Por eso las dos
 * de aquí abajo llevan `requireWorkspaceId()` además de la cuenta de filas —
 * con el respaldo 'default' el filtro no encaja con ninguna factura y el 204
 * vacío se leía como «cobrada».
 */
export async function updateContractInvoiceDoc(id: string, updates: any) {
    const wsId = requireWorkspaceId();
    await exigirFilas(
        supabase.from('contract_invoices')
            .update({ ...toSnake(updates), updated_at: new Date().toISOString() })
            .eq('id', id).eq('workspace_id', wsId).select('id'),
        'updateContractInvoiceDoc', `Contract invoice ${id}`, 'updated',
    );
}

export async function deleteContractInvoiceDoc(id: string) {
    const wsId = requireWorkspaceId();
    await exigirFilas(
        supabase.from('contract_invoices')
            .delete().eq('id', id).eq('workspace_id', wsId).select('id'),
        'deleteContractInvoiceDoc', `Contract invoice ${id}`, 'deleted',
    );
}

export async function addServiceVisitDoc(data: any) {
    const payload = { ...toSnake(data), workspace_id: requireWorkspaceId() };
    console.log('[addServiceVisitDoc] Inserting:', payload);
    const { data: inserted, error } = await supabase.from('service_visits').insert(payload).select().single();
    if (error) {
        console.error('[addServiceVisitDoc] Error:', error);
        throw new Error(error.message);
    }
    return inserted?.id || 'stub-id';
}
export async function updateServiceVisitDoc(id: string, updates: Partial<ServiceVisit>) {
    await exigirFilas(
        supabase.from('service_visits').update(toSnake(updates)).eq('id', id).eq('workspace_id', getWorkspaceId()).select('id'),
        'updateServiceVisitDoc', `Service visit ${id}`, 'updated',
    );
}
export async function deleteServiceVisitDoc(id: string) {
    const { data, error } = await supabase.from('service_visits').delete().eq('id', id).eq('workspace_id', getWorkspaceId()).select('id');
    if (error) {
        console.error('[deleteServiceVisitDoc] Error:', error);
        throw new Error(error.message);
    }
    if (!data || data.length === 0) {
        throw new Error(`Service visit ${id} not deleted (0 rows) — workspace mismatch or RLS policy blocked the delete.`);
    }
}
/**
 * Reordena la ruta. Aquí no había NINGUNA comprobación: ni `error` ni filas
 * escritas, así que un reorden a medias —tres visitas movidas de siete— se veía
 * igual que uno completo, y la ruta quedaba con dos visitas compartiendo
 * `sequence_index` hasta que alguien la miraba en la calle.
 *
 * Se recogen los resultados en vez de dejar que `Promise.all` reviente en el
 * primero: se lanzan todas de todas formas, y así el mensaje puede nombrar
 * TODAS las que fallaron en lugar de la que llegó antes. Recogerlas también
 * evita el «unhandled rejection» de las hermanas que caen después.
 */
export async function reorderServiceVisitDocs(visitIds: string[]) {
    const wsId = getWorkspaceId();
    const resultados = await Promise.all(visitIds.map(async (id, index) => {
        const { data, error } = await supabase.from('service_visits')
            .update({ sequence_index: index })
            .eq('id', id).eq('workspace_id', wsId)
            .select('id');
        return { id, data, error };
    }));
    const fallidas = resultados.filter(r => r.error || !r.data || r.data.length === 0);
    if (fallidas.length > 0) {
        for (const f of fallidas) {
            console.error('[reorderServiceVisitDocs] visita sin reordenar:', f.id, f.error ?? '(0 filas)');
        }
        const msg = `Service visits not reordered (${fallidas.length} of ${visitIds.length} failed): ${fallidas.map(f => f.id).join(', ')} — the route order is now inconsistent.`;
        throw new Error(msg);
    }
}

// --- SERVICE REQUESTS ---
export async function fetchServiceRequests(wsId: string): Promise<ServiceRequest[]> {
    const { data, error } = await supabase.from('service_requests').select('*').eq('workspace_id', wsId).order('created_at', { ascending: false });
    if (error) { console.error('[fetchServiceRequests] error:', error.message); throw new Error(error.message); }
    return mapDocs(data);
}
export async function addServiceRequestDoc(data: any) {
    const { data: inserted, error } = await supabase.from('service_requests').insert({ ...toSnake(data), workspace_id: requireWorkspaceId() }).select().single();
    if (error) {
        console.error('[addServiceRequestDoc] Error:', error);
        throw new Error(error.message);
    }
    return inserted?.id || 'stub-id';
}
export async function updateServiceRequestDoc(id: string, updates: Partial<ServiceRequest>) {
    await exigirFilas(
        supabase.from('service_requests').update(toSnake(updates)).eq('id', id).eq('workspace_id', getWorkspaceId()).select('id'),
        'updateServiceRequestDoc', `Service request ${id}`, 'updated',
    );
}
export async function deleteServiceRequestDoc(id: string) {
    const { error } = await supabase.from('service_requests').delete().eq('id', id).eq('workspace_id', getWorkspaceId());
    if (error) throw new Error(error.message);
}

// --- SERVICE VISIT HISTORY (Paginated) ---
export async function fetchVisitHistory(wsId: string, options?: { clientId?: string; contractId?: string; limit?: number; offset?: number }): Promise<ServiceVisit[]> {
    let query = supabase.from('service_visits').select('*').eq('workspace_id', wsId).order('date', { ascending: false });
    if (options?.clientId) query = query.eq('client_id', options.clientId);
    if (options?.contractId) query = query.eq('contract_id', options.contractId);
    if (options?.limit) query = query.limit(options.limit);
    if (options?.offset) query = query.range(options.offset, options.offset + (options.limit || 20) - 1);
    const { data, error } = await query;
    // PGRST103 (416) es «el rango pedido empieza más allá de la última fila»:
    // pedir la página siguiente cuando ya no hay más. Eso es una página VACÍA,
    // no una avería — lanzarlo convertiría el final de la lista paginada en un
    // error rojo. Es el único lector de aquí que usa `.range()`, y por eso el
    // único que necesita esta salida.
    if (error?.code === 'PGRST103') return [];
    if (error) { console.error('[fetchVisitHistory] error:', error.message); throw new Error(error.message); }
    return mapDocs(data);
}

// ============================================================
//  TIME CLOCK
// ============================================================
/** Ventana de lectura de `fetchTimeLogs`, en días. Un año cubre el costeo de
 *  cualquier obra viva; lo anterior se consulta con `fetchTimeLogsRange`. */
const TIME_LOGS_WINDOW_DAYS = 365;
/** Tope explícito para que un recorte sea visible en vez de silencioso. */
const TIME_LOGS_MAX_ROWS = 5000;

/**
 * Todos los turnos del taller dentro de la ventana de costeo.
 *
 * No llevaba ni tope de fecha ni ORDER BY, y la alimenta tanto el costeo de
 * Finanzas como el Mapa del Reloj sobre la MISMA clave de caché. Pasado el
 * `max-rows` de PostgREST la respuesta se corta sin error ninguno: el mapa
 * perdía los turnos más antiguos y el P&L de cada obra cobraba de menos la
 * mano de obra sin que nada avisara. Con ventana y orden explícito, si algo se
 * recorta se recorta por el extremo viejo y a propósito, y el aviso lo deja
 * dicho en consola en lugar de dejarlo mudo.
 *
 * El taller llega por parámetro OBLIGATORIO. Antes no llevaba ninguno «a
 * propósito», porque `useAllTimeLogs` la pasaba tal cual como `queryFn` de
 * React Query y React Query invoca con su propio contexto como primer
 * argumento. Con el parámetro ese hook TIENE que envolverla en una flecha
 * —`queryFn: () => fetchTimeLogs(wsId)`— o le cuela el objeto de contexto donde
 * va el id del taller. Lo que se gana a cambio: ya no se lee 'default' aquí
 * dentro, que es un taller inexistente y otra lista vacía silenciosa.
 */
export async function fetchTimeLogs(wsId: string): Promise<TimeLog[]> {
    const desde = new Date(Date.now() - TIME_LOGS_WINDOW_DAYS * 24 * 60 * 60 * 1000).toISOString();
    const { data, error } = await supabase.from('time_logs')
        .select('*')
        .eq('workspace_id', wsId)
        .gte('clock_in', desde)
        .order('clock_in', { ascending: false })
        .limit(TIME_LOGS_MAX_ROWS);
    if (error) { console.error('[fetchTimeLogs] error:', error.message); throw new Error(error.message); }
    if (data && data.length >= TIME_LOGS_MAX_ROWS) {
        console.warn(`[fetchTimeLogs] tope de ${TIME_LOGS_MAX_ROWS} filas alcanzado: el costeo de mano de obra está mirando una ventana recortada.`);
    }
    return mapDocs(data);
}

/**
 * Filtro PostgREST para «los turnos de esta persona».
 *
 * `user_id` (el uid de auth) y `allowed_user_id` (la identidad de trabajador)
 * son dos espacios de identidad distintos: el fichaje los escribe por separado
 * y en producción difieren en 87 de 98 filas. Filtrar solo por `user_id` —lo
 * que se hacía aquí— dejaba sin reloj, para siempre, a cualquiera que hubiera
 * sido reinvitado. Se piden los dos, que son las mismas dos ramas que acepta
 * `isLogForUser` en services/timeLogAdapter.ts.
 */
export const userIdentityFilter = (uid: string) => `allowed_user_id.eq.${uid},user_id.eq.${uid}`;

/**
 * One-shot read of the currently-open shifts, using the same visibility rules as
 * the live subscription.
 *
 * Exists so clock in/out can refresh the UI immediately instead of waiting on a
 * realtime event.
 *
 * Este comentario decía que `time_logs` NO estaba en la publicación
 * `supabase_realtime`. Sí lo está: se activó desde el panel de Supabase, no por
 * una migración de este repo —por eso no aparece en `supabase/migrations/` y por
 * eso se creyó lo contrario—. El canal SÍ dispara.
 *
 * Aun así este refresco explícito se queda, a propósito: es el tirante junto al
 * cinturón. Un evento en vivo se pierde si el socket está reconectando, si la
 * pestaña volvió de segundo plano o si alguien vuelve a tocar la publicación en
 * el panel; y en el reloj el precio de perderlo es que el panel se quede con
 * datos viejos, que el usuario lee como «no me marcó la entrada». No lo quites
 * dando por hecho que el tiempo real basta.
 */
/** `wsId` OBLIGATORIO, como en el resto de lectores de este archivo. Con el
 *  respaldo `getWorkspaceId()` aquí dentro, un refresco disparado justo al
 *  cambiar de taller leía el ANTERIOR: el efecto que lo lanza vive dentro de
 *  `WorkspaceProvider` y React vacía los efectos de dentro hacia fuera, así que
 *  corre ANTES de que el id nuevo llegue al localStorage. */
export async function fetchOpenTimeLogs(role: any, uid: string, wsId: string): Promise<TimeLog[]> {
    const isPrivileged = role === 'Admin' || role === 'Office';
    // Sin identidad y sin privilegio no hay nada que enseñar, y se corta ANTES de
    // montar la consulta: un filtro que se cae deja al trabajador viendo los
    // turnos de todo el taller.
    if (!isPrivileged && !uid) return [];
    let q = supabase.from('time_logs')
        .select('*')
        .eq('workspace_id', wsId)
        .is('clock_out', null);
    if (!isPrivileged) q = q.or(userIdentityFilter(uid));
    const { data, error } = await q;
    if (error) { console.error('[fetchOpenTimeLogs] error:', error.message); throw new Error(error.message); }
    return mapDocs(data);
}

/** `taller` OBLIGATORIO: de él salen el nombre del canal (`active_logs:<wsId>`)
 *  y el filtro `workspace_id=eq.<wsId>`. Leerlo aquí dentro con
 *  `getWorkspaceId()` ataba la escucha al taller ANTERIOR al cambiar de taller
 *  —el efecto suscriptor corre antes de que WorkspaceContext escriba el id
 *  nuevo— y nada volvía a ejecutarse después: escribir en localStorage no
 *  provoca ningún renderizado. */
export function fetchActiveTimeLogs(role: any, uid: string, callback: (logs: TimeLog[]) => void, taller: string): () => void {
    const wsId = taller;
    const isPrivileged = role === 'Admin' || role === 'Office';

    const runQuery = () => {
        if (!isPrivileged && !uid) { callback([]); return; }
        let q = supabase.from('time_logs')
            .select('*')
            .eq('workspace_id', wsId)
            .is('clock_out', null);
        if (!isPrivileged) q = q.or(userIdentityFilter(uid));
        // `if (data)` a secas dejaba el panel congelado con datos viejos: si la
        // consulta fallaba, la respuesta traía `data: null` y la devolución de
        // llamada NO se invocaba nunca — ni lista nueva, ni aviso, ni una línea
        // en consola. El reloj en vivo se quedaba en la última foto buena y
        // nadie tenía forma de saber que había dejado de refrescarse.
        q.then(({ data, error }) => {
            if (error) { console.error('[fetchActiveTimeLogs] error:', error.message); return; }
            callback(mapDocs(data));
        });
    };

    runQuery();

    // Channel name must be unique per workspace so two tabs in different
    // workspaces don't cross-pollinate the live feed.
    // On (re)connect, re-run the query so we catch up on anything that
    // changed while disconnected — Supabase realtime does not replay missed events.
    const channel = supabase.channel(`active_logs:${wsId}`)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'time_logs', filter: `workspace_id=eq.${wsId}` }, runQuery)
        .subscribe((status) => {
            if (status === 'SUBSCRIBED') runQuery();
        });

    return () => {
        supabase.removeChannel(channel);
    };
}

/** Forma mínima de fila que necesita el barrendero para decidir. */
type SegmentoAbierto = { id: string; clock_in: string; clock_out?: string | null; shift_group_id?: string | null };

/**
 * `shift_group_id` → el `clock_in` más antiguo de ese grupo.
 *
 * Un turno es una o más filas con el mismo `shift_group_id`; las históricas son
 * grupo de una y salen igual de aquí.
 */
export function earliestClockInByGroup(rows: { shift_group_id?: string | null; clock_in?: string | null }[]): Record<string, string> {
    const out: Record<string, string> = {};
    for (const r of rows) {
        if (!r.shift_group_id || !r.clock_in) continue;
        const previo = out[r.shift_group_id];
        if (!previo || Date.parse(r.clock_in) < Date.parse(previo)) out[r.shift_group_id] = r.clock_in;
    }
    return out;
}

/**
 * De los tramos abiertos, los que ya pasaron el corte.
 *
 * El corte se mide contra el primer `clock_in` del GRUPO, no contra el del
 * tramo: en cuanto existen traspasos, un turno de veinte horas puede tener su
 * último tramo abierto hace diez minutos, y midiendo por tramo no se cerraría
 * nunca. Sin grupo conocido se cae al `clock_in` propio, que es el caso de las
 * filas antiguas.
 *
 * Comparación por `Date.parse` y no lexicográfica: PostgREST devuelve
 * `...+00:00` y `toISOString()` produce `...Z`, y como cadenas ordenan al revés.
 */
export function staleOpenSegments<T extends SegmentoAbierto>(
    open: T[], groupStarts: Record<string, string>, cutoffMs: number,
): T[] {
    return open.filter(r => {
        const inicio = (r.shift_group_id && groupStarts[r.shift_group_id]) || r.clock_in;
        return Date.parse(inicio) < cutoffMs;
    });
}

/**
 * Cierra los tramos que llevan abiertos más de `maxHours` (14 por defecto):
 * fichajes de salida que alguien olvidó. Se llama una vez por arranque desde
 * App.tsx para que no envenenen el panel en vivo para siempre.
 *
 * Quedan marcados `status='Auto-closed'` y `closed_reason='auto'` para que la
 * oficina los distinga de un cierre de verdad y los corrija en
 * TimeLogEditorModal.
 *
 * Dos cosas que esta función hacía mal, las dos caras:
 *
 * 1. Escribía `clock_out = clock_in + 8h` y `duration_minutes = 480`. En
 *    producción eso son 26 filas —208 horas, cerca de la mitad de todo lo
 *    registrado— de jornadas que nadie trabajó, y nadie las revisaba
 *    justamente porque el número parecía razonable. Ahora se cierra con
 *    duración en NULO: no se inventa nada.
 *
 *    Con la precisión que hace falta, porque «hueco visible» prometía de más:
 *    los lectores de horas devuelven 0 para esta fila, no un vacío —no se
 *    puede cobrar lo que nadie sabe—, así que en un TOTAL se ve como cero.
 *    Lo que la distingue de un cero de verdad es `status = 'Auto-closed'` y
 *    `duration_minutes IS NULL`: eso es lo que lee `necesitaRevision()` en
 *    components/workforce/timesheet-utils.ts, y con eso el panel de horas la
 *    marca y la puede filtrar. Sin esa marca, esto sería cambiar 480 minutos
 *    inventados por 0 minutos inventados.
 * 2. Era la única función del archivo sin `.eq('workspace_id', ...)`: un solo
 *    arranque barría todos los talleres que el usuario alcanzara a ver.
 *
 * Usa `requireWorkspaceId()` y no `getWorkspaceId()`: este último devuelve
 * 'default' hasta que WorkspaceContext resuelve, y App.tsx dispara esto con
 * `isAuthorized`, que es la MISMA señal que arranca esa carga. Con 'default' el
 * SELECT no traía nada, respondía `error: null`, devolvía 0 y el efecto no se
 * vuelve a disparar: en un arranque en frío el barrendero no corría nunca y
 * nadie se enteraba. Lanzando, App.tsx lo registra.
 *
 * Devuelve cuántos tramos se cerraron DE VERDAD: filas devueltas por el UPDATE,
 * no ausencia de error — un PATCH que no toca ninguna fila responde `error:
 * null` igual que uno que sí.
 */
export async function closeStaleShifts(maxHours: number = 14): Promise<number> {
    const wsId = requireWorkspaceId();
    const cutoffMs = Date.now() - maxHours * 60 * 60 * 1000;

    const { data: abiertos, error } = await supabase
        .from('time_logs')
        .select('id, clock_in, clock_out, shift_group_id')
        .eq('workspace_id', wsId)
        .is('clock_out', null);
    if (error) {
        console.warn('[closeStaleShifts] select error:', error.message);
        return 0;
    }
    if (!abiertos || abiertos.length === 0) return 0;

    // Los hermanos ya cerrados del mismo turno: son los que sostienen la fecha
    // real de inicio cuando el tramo abierto es un traspaso reciente.
    let inicios: Record<string, string> = {};
    const grupos = [...new Set((abiertos as SegmentoAbierto[]).map(r => r.shift_group_id).filter(Boolean))] as string[];
    if (grupos.length > 0) {
        const { data: hermanos, error: grpErr } = await supabase
            .from('time_logs')
            .select('shift_group_id, clock_in')
            .eq('workspace_id', wsId)
            .in('shift_group_id', grupos);
        if (grpErr) {
            // Sin los hermanos se mide por tramo: cierra de menos, nunca de más.
            console.warn('[closeStaleShifts] group select error:', grpErr.message);
        } else {
            inicios = earliestClockInByGroup(hermanos || []);
        }
    }

    const vencidos = staleOpenSegments(abiertos as SegmentoAbierto[], inicios, cutoffMs);
    if (vencidos.length === 0) return 0;

    let closed = 0;
    for (const row of vencidos) {
        const { data: escrito, error: upErr } = await supabase
            .from('time_logs')
            .update({
                // Mismo instante que la entrada: cero horas, no ocho inventadas.
                clock_out: row.clock_in,
                duration_minutes: null,
                status: 'Auto-closed',
                closed_reason: 'auto',
            })
            .eq('id', row.id)
            .eq('workspace_id', wsId)
            // Si la persona fichó salida entre el SELECT y este UPDATE, su hora
            // real gana: el barrendero no debe pisarla.
            .is('clock_out', null)
            .select('id');
        if (upErr) {
            // Con el id a la vista: si un CHECK rechaza el cierre, el tramo se
            // queda abierto y alguien tiene que poder ir a buscarlo.
            console.warn(`[closeStaleShifts] update error on ${row.id}:`, upErr.message);
            continue;
        }
        closed += escrito?.length ?? 0;
    }
    if (closed > 0) console.log(`[closeStaleShifts] auto-closed ${closed} stale shift(s)`);
    return closed;
}
/** `wsId` OBLIGATORIO por lo mismo que sus hermanas de aquí arriba. */
export async function fetchTimeLogsRange(daysBack: number, wsId: string): Promise<TimeLog[]> {
    const msBack = daysBack * 24 * 60 * 60 * 1000;
    const dateLimit = new Date(Date.now() - msBack).toISOString();
    const { data, error } = await supabase.from('time_logs').select('*').eq('workspace_id', wsId).gte('clock_in', dateLimit);
    if (error) { console.error('[fetchTimeLogsRange] error:', error.message); throw new Error(error.message); }
    return mapDocs(data);
}
/** `maybeSingle` y no `single`: con cero filas `single` contesta PGRST116, y
 *  «no está» al LEER no es una avería — lanzarlo convertiría un no-encontrado
 *  de trámite en error para todos los llamadores. Con `maybeSingle` cero filas
 *  llegan como `data: null` y `error: null`, y lo que queda en `error` es un
 *  fallo de verdad, que sí hay que contar. */
export async function getTimeLogDoc(wsId: string, id: string): Promise<TimeLog | null> {
    const { data, error } = await supabase.from('time_logs').select('*').eq('id', id).eq('workspace_id', wsId).maybeSingle();
    if (error) { console.error('[getTimeLogDoc] error:', error.message); throw new Error(error.message); }
    return data ? toCamel(data) : null;
}
export async function addTimeLogDoc(data: any) {
    // identity_v2 dual-write: derive new-shape fields from existing payload so
    // callers don't have to know about them yet. After L6 read-switch completes
    // every caller will pass these explicitly and this derivation becomes a no-op.
    const enriched = { ...data };
    if (!enriched.allowedUserId && enriched.userId) {
        enriched.allowedUserId = enriched.userId;
    }
    if (!enriched.userNameSnapshot && enriched.userName) {
        enriched.userNameSnapshot = enriched.userName;
    }
    if (!enriched.targetLabel) {
        enriched.targetLabel = enriched.projectName ?? 'General time';
    }
    const { data: inserted, error } = await supabase.from('time_logs').insert({ ...toSnake(enriched), workspace_id: requireWorkspaceId() }).select().single();
    if (error) {
        // 23505 = unique_violation. The partial unique index `one_open_shift_per_user`
        // fires when the worker already has a shift with clock_out IS NULL. This is a
        // BY-DESIGN guard, not a bug — throw the friendly marker WITHOUT console.error
        // so it never pollutes the admin error panel (it's already shown to the user as a toast).
        if ((error as any).code === '23505' && /one_open_shift_per_user/.test(error.message)) {
            throw new Error('ALREADY_OPEN_SHIFT');
        }
        /* Los rechazos del disparador `time_logs_target_guard` son la misma
           clase de cosa: la base defendiéndose, no un fallo del sistema. Sin
           esto, el primer peón que fichara contra una obra borrada —hay 6 filas
           así en producción— dejaba un reporte de error en el panel del admin,
           porque `consolePatch` reenvía todo console.error a system_error_logs.
           El mensaje ES el marcador (el RAISE del servidor lo pone así). */
        if (['TARGET_NOT_FOUND', 'NOT_ASSIGNED_TO_PROJECT'].includes(error.message)) {
            throw new Error(error.message);
        }
        console.error('[addTimeLogDoc] Error:', error);
        throw new Error(error.message);
    }
    return inserted?.id || 'stub-id';
}
/**
 * Los rechazos ESPERADOS de un traspaso de turno. Cada uno es la base
 * defendiéndose —el turno no es tuyo, sigues en el lonche, esa obra ya no
 * existe—, no un fallo del sistema, y por eso ninguno pasa por `console.error`.
 *
 * El mensaje del error ES el marcador: la RPC lanza P0001 poniendo el marcador
 * solo en MESSAGE y la prosa explicativa en DETAIL, justamente para que esta
 * capa no tenga que reconocer un rechazo leyendo texto en prosa.
 *
 * `TARGET_NOT_FOUND` y `NOT_ASSIGNED_TO_PROJECT` pueden venir también del
 * disparador `time_logs_target_guard`, que vigila el INSERT del tramo nuevo.
 */
export const TRANSFER_MARKERS = [
    'SHIFT_NOT_FOUND', 'SHIFT_NOT_OPEN', 'NOT_YOUR_SHIFT', 'ON_LUNCH',
    'ALREADY_ON_TARGET', 'INVALID_TARGET_KIND', 'TARGET_NOT_FOUND',
    'NO_OFFICE_CONFIGURED', 'TRANSFER_BEFORE_CLOCK_IN', 'TRANSFER_IN_FUTURE',
    'NOT_ASSIGNED_TO_PROJECT',
] as const;
export type TransferMarker = typeof TRANSFER_MARKERS[number];

/**
 * Destinos ACEPTADOS por `transfer_shift`. Ojo: no son los mismos que
 * `TargetType` de `services/timeLogAdapter.ts`.
 *
 * `'general'` es un valor de LECTURA —lo que la columna generada `target_type`
 * produce cuando no hay ningún id puesto— y el RPC lo rechaza de plano con
 * INVALID_TARGET_KIND. Tenerlo aquí prometía en el tipo un traslado que el
 * servidor nunca iba a hacer, y el compilador habría dejado pasar el error
 * hasta la pantalla del trabajador. Para «volver al taller» el destino es
 * `'office'`.
 */
export type TransferTargetKind = 'project' | 'contract' | 'office';

export interface TransferShiftArgs {
    /** El tramo ABIERTO que se va a cerrar para abrir el siguiente. */
    logId: string;
    kind: TransferTargetKind;
    /** Vacío o ausente para 'office' (se resuelve por taller) y para 'general'. */
    targetId?: string | null;
    /** Dónde estaba el teléfono al pulsar. Vale para el cierre y para la apertura:
     *  el traspaso ocurre en un solo instante y un solo sitio. */
    location?: { lat: number; lng: number } | null;
    isOffSite?: boolean;
    distanceFromSiteFt?: number | null;
    /** Fuera de geocerca respecto al destino que se ABANDONA. Sin esto, un
     *  traspaso borraba el dato de que el tramo cerrado terminó fuera de obra. */
    outOffSite?: boolean | null;
    outDistanceFromSiteFt?: number | null;
}

/**
 * Pasa el reloj de un destino a otro sin obligar a fichar salida y entrada.
 *
 * La cuenta la lleva el servidor en UNA transacción (`transfer_shift`) porque
 * partirla en dos llamadas desde el móvil no funciona en ninguno de los dos
 * órdenes: abrir antes de cerrar choca con el índice `one_open_shift_per_user`,
 * y cerrar antes de abrir deja a la persona SIN turno si la segunda llamada no
 * sale —mala cobertura, pantalla bloqueada— y encima el barrendero le cierra
 * el tramo a las catorce horas.
 *
 * Devuelve el tramo NUEVO ya abierto, para que quien llame no tenga que
 * adivinar su id ni esperar a que baje por realtime.
 */
export async function transferShiftDoc(args: TransferShiftArgs): Promise<TimeLog> {
    const { data, error } = await supabase.rpc('transfer_shift', {
        p_log_id: args.logId,
        p_target_kind: args.kind,
        // `|| null` y no `?? null`: la cadena vacía no es NULL. 'office' y
        // 'general' llegan sin id, y un `''` chocaría con el CHECK
        // `target_ids_not_blank` — el mismo `''` que ya contabilizó 42,6 h de
        // taller como horas de obra antes de la migración.
        p_target_id: args.targetId || null,
        // El `default now()` de la RPC solo se aplica cuando el argumento NO
        // viaja, y PostgREST manda toda clave presente en el cuerpo: un
        // `p_at: null` llegaba como NULL y se saltaba el valor por defecto. El
        // servidor ya lo coalesce (defecto corregido), pero la hora buena es la
        // que vio el teléfono del trabajador, así que va explícita igualmente.
        p_at: new Date().toISOString(),
        p_location: args.location ?? null,
        p_off_site: args.isOffSite ?? false,
        p_distance_ft: args.distanceFromSiteFt ?? null,
        p_out_off_site: args.outOffSite ?? null,
        p_out_distance_ft: args.outDistanceFromSiteFt ?? null,
    });

    if (error) {
        /* Mismo trato que en `addTimeLogDoc`: un rechazo esperado se relanza SIN
           console.error, porque `utils/consolePatch.ts` reenvía todo
           console.error a system_error_logs. Sin esta rama, cada peón que
           intentara pasarse a una obra a la que no está asignado abriría un
           reporte de error en el panel del admin. */
        if ((TRANSFER_MARKERS as readonly string[]).includes(error.message)) {
            throw new Error(error.message);
        }
        console.error('[transferShiftDoc] Error:', error);
        throw new Error(error.message);
    }

    // `toCamel` devuelve los arrays TAL CUAL (mira su primera línea). La RPC
    // declara `returns time_logs`, así que PostgREST manda un objeto; si algún
    // día pasara a `setof`, sin este desempaquetado el llamador recibiría un
    // array disfrazado de TimeLog, con todos sus campos en undefined y sin un
    // solo error por ninguna parte.
    const fila = Array.isArray(data) ? data[0] : data;
    if (!fila) {
        console.error('[transferShiftDoc] la RPC no devolvió fila', { logId: args.logId });
        throw new Error('TRANSFER_NO_ROW');
    }
    return toCamel(fila) as TimeLog;
}

/**
 * El fichaje de SALIDA pasa por aquí, y por eso esta función mira el número de
 * filas escritas y no solo el error.
 *
 * PostgREST responde `error: null` a un PATCH que no encaja con NINGUNA fila
 * —id que ya no existe, workspace equivocado, RLS que deniega—. Con la
 * comprobación vieja la salida decía «guardado» sin haber escrito nada, el
 * turno se quedaba abierto, y a las catorce horas el barrendero lo cerraba
 * inventándole la jornada. `.select('id')` obliga a que la base devuelva lo
 * que tocó, y sin filas esto revienta: offlineSync lo reencola y el usuario ve
 * el fallo.
 *
 * `requireWorkspaceId()` y no `getWorkspaceId()`: la entrada ya usaba el
 * estricto y la salida no, así que con el localStorage desalojado la salida
 * apuntaba al workspace 'default' y no encajaba con nada.
 */
export async function updateTimeLogDoc(id: string, updates: Partial<TimeLog>) {
    const wsId = requireWorkspaceId();   // antes de montar nada: si el taller no está, no se escribe
    const { data, error } = await supabase
        .from('time_logs')
        .update(toSnake(updates))
        .eq('id', id)
        .eq('workspace_id', wsId)
        .select('id');
    if (error) {
        // 23505 sobre `one_open_shift_per_user`: lo dispara la rama de reapertura
        // de TimeLogEditorModal (poner clock_out a NULL con otro turno abierto).
        // Es la guarda funcionando, no un fallo — marcador amable y SIN
        // console.error, para que no ensucie el panel de errores del admin.
        if ((error as any).code === '23505' && /one_open_shift_per_user/.test(error.message)) {
            throw new Error('ALREADY_OPEN_SHIFT');
        }
        // 23514 = check_violation: salida antes de la entrada, comida
        // incoherente, dos destinos a la vez. También es la base defendiéndose.
        if ((error as any).code === '23514') {
            throw new Error('INVALID_TIME_LOG');
        }
        console.error('[updateTimeLogDoc] Error:', error);
        throw new Error(error.message);
    }
    if (!data || data.length === 0) {
        console.error('[updateTimeLogDoc] 0 filas escritas', { id });
        throw new Error('TIME_LOG_NOT_UPDATED');
    }
}
export async function deleteTimeLogDoc(id: string) {
    const { error } = await supabase.from('time_logs').delete().eq('id', id).eq('workspace_id', getWorkspaceId());
    if (error) { console.error('[deleteTimeLogDoc] Error:', error); throw new Error(error.message); }
}
export async function fetchTimeLogsByProject(wsId: string, projectId: string): Promise<TimeLog[]> {
    const { data, error } = await supabase.from('time_logs').select('*').eq('project_id', projectId).eq('workspace_id', wsId);
    if (error) { console.error('[fetchTimeLogsByProject] error:', error.message); throw new Error(error.message); }
    return mapDocs(data);
}
/**
 * Propaga un cambio de nombre al turno EN CURSO. Único llamador:
 * views/settings/MyAccountSection.tsx, tras renombrarse uno mismo.
 *
 * `clock_out is null` acota lo que antes era un UPDATE a pelo sobre todo el
 * historial de la persona. Los tramos ya cerrados guardan
 * `user_name_snapshot`, que existe justamente para conservar el nombre que
 * tenía el trabajador ese día: reescribirlo borra el dato, y de paso convierte
 * un cambio de nombre en una escritura masiva sin cota ninguna.
 *
 * SIN guarda de cero filas, y a propósito. Aquí cero filas es el resultado
 * NORMAL: quien se renombra fuera de jornada no tiene ningún turno abierto que
 * propagar. Poner la cuenta de filas convertiría el caso corriente en un aviso
 * de error rojo en «Mi cuenta» justo después de un renombrado que sí funcionó.
 */
export async function batchUpdateTimeLogs(userId: string, updates: Partial<TimeLog>): Promise<void> {
    const { error } = await supabase.from('time_logs').update(toSnake(updates)).eq('user_id', userId).eq('workspace_id', getWorkspaceId()).is('clock_out', null);
    if (error) { console.error('[batchUpdateTimeLogs] Error:', error); throw new Error(error.message); }
}

// ============================================================
//  TEAM / CREWS
// ============================================================
export async function fetchTeamMembers(workspaceId: string): Promise<TeamMember[]> {
    return fetchTeamMembersByWorkspace(workspaceId);
}
export async function addTeamMemberDoc(data: any, workspaceId?: string) {
    if (!workspaceId) {
        throw new Error("workspaceId is required to add team member securely");
    }
    console.log('Adding team member via Edge Function:', data);

    const { data: result, error } = await supabase.functions.invoke('invite-user', {
        body: {
            email: data.email,
            name: data.name,
            role: data.role,
            workspaceIds: [workspaceId],
            allowedViews: data.allowedViews || []
        }
    });

    if (error) {
        // supabase-js collapses ANY non-2xx into the same opaque
        // "Edge Function returned a non-2xx status code". The function's real
        // message ("email already invited", "not an admin of this workspace",
        // a missing server key…) is in the response body, which is only
        // reachable through error.context — without unwrapping it the user is
        // told they lack permission even when the cause is something else
        // entirely. Same unwrap the AI chat client already does.
        let detail = error.message;
        let status: number | undefined;
        try {
            if ((error as any).context) {
                const ctx = (error as any).context;
                status = ctx.status;
                const body = await ctx.json();
                detail = body?.error || body?.message || body?.debug_detail || detail;
            }
        } catch { /* body wasn't JSON — keep the generic message */ }

        console.error('[invite-user] failed:', { status, detail, error });
        throw new Error(status ? `${detail} (HTTP ${status})` : detail);
    }

    return 'invited-' + Date.now();
}
export async function updateTeamMemberDoc(id: string, updates: Partial<TeamMember>, workspaceId?: string) {
    if (workspaceId) {
        const { error } = await supabase.rpc('update_workspace_user', {
            target_user_id: id,
            target_workspace_id: workspaceId,
            new_role: updates.role || null,
            new_name: updates.name || null,
            new_views: updates.allowedViews || null,
            new_photo_url: updates.photoUrl || null
        });
        if (error) {
            console.error('Error updating team member via RPC:', error);
            throw error;
        }
    } else {
        await supabase.from('allowed_users').update(toSnake(updates)).eq('id', id);
    }
}
// C1: self-service profile update. Unlike update_workspace_user (admin-gated, above), this
// RPC is scoped ENTIRELY by the caller's own JWT email, so any authenticated member can save
// their OWN name/photo and it can never write another user's row. Returns rows affected
// (0 = the RPC ran but matched nothing, e.g. email mismatch / not-yet-applied). Use for
// SELF edits only — editing OTHER members must keep going through updateTeamMemberDoc.
export async function updateMyProfile(name: string | null, photoUrl: string | null): Promise<number> {
    const { data, error } = await supabase.rpc('update_my_profile', {
        new_name: name,
        new_photo_url: photoUrl,
    });
    if (error) {
        console.error('[updateMyProfile] Error:', error);
        throw error;
    }
    return (data as number) ?? 0;
}
export async function deleteTeamMemberDoc(id: string, workspaceId?: string) {
    if (workspaceId) {
        // Use secure RPC that verifies caller is an Admin of the workspace
        const { error } = await supabase.rpc('delete_workspace_user', {
            target_user_id: id,
            target_workspace_id: workspaceId
        });
        if (error) {
            console.error('Error deleting team member via RPC:', error);
            throw error;
        }
    } else {
        // Fallback for super admin direct delete
        const { error } = await supabase.from('allowed_users').delete().eq('id', id);
        if (error) {
            console.error('Error deleting team member:', error);
            throw error;
        }
    }
}
export async function removeTeamMemberDoc(id: string, workspaceId?: string) {
    if (workspaceId) {
        const { error } = await supabase.rpc('remove_user_from_workspace', {
            target_user_id: id,
            target_workspace_id: workspaceId
        });
        if (error) {
            console.error('Error removing team member via RPC:', error);
            throw error;
        }
    } else {
        const { error, count } = await supabase.from('allowed_users').delete({ count: 'exact' }).eq('id', id);
        if (error) {
            console.error('Error removing team member:', error);
            throw error;
        }
        if (count === 0) {
            console.warn('Remove returned 0 rows affected — RLS policy may be blocking deletion for id:', id);
        }
    }
}

/**
 * Las cuadrillas del taller ACTIVO.
 *
 * Aquí decía que el RLS acotaba solo por taller, y no es cierto: el `qual` de
 * `tenant_iso_crews` es `workspace_id IN (SELECT get_my_workspace_ids())`, o
 * sea TODOS los talleres a los que pertenece la persona, no aquel en el que
 * está. Quien trabaja en dos ve las cuadrillas de los dos mezcladas y sin nada
 * que las distinga. Hoy no se nota —18 usuarios con dos talleres, ninguno con
 * cuadrillas en ambos— pero eso es una coincidencia de los datos de hoy, no una
 * garantía.
 */
export async function fetchCrews(wsId: string): Promise<Crew[]> {
    const { data, error } = await supabase.from('crews').select('*').eq('workspace_id', wsId);
    if (error) { console.error('[fetchCrews] error:', error.message); throw new Error(error.message); }
    return mapDocs(data);
}
export async function addCrewDoc(data: any) {
    const payload: Record<string, any> = {
        name: data.name,
        color: data.color || '#059669',
        workspace_id: requireWorkspaceId(),
    };
    if (data.memberIds && data.memberIds.length > 0) payload.member_ids = data.memberIds;
    if (data.leaderId) payload.leader_id = data.leaderId;
    const { data: inserted, error } = await supabase.from('crews').insert(payload).select().single();
    if (error) { console.error('[addCrewDoc] Error:', error); throw new Error(error.message); }
    return inserted?.id || 'stub-id';
}
export async function updateCrewDoc(id: string, updates: Partial<Crew>) {
    // Sin filtro de taller, y el comentario que había aquí decía dos cosas
    // falsas: que el alcance lo pone el RLS `por tenant_id` —la política es
    // `tenant_iso_crews`, sobre `workspace_id`— y que `fetchCrews` hace lo
    // mismo, que ya no: fetchCrews acota el taller en la consulta desde esta
    // misma unidad. Y ese `qual` es `workspace_id IN (SELECT
    // get_my_workspace_ids())`: TODOS los talleres de la persona, no aquel en
    // el que está. O sea que aquí el RLS no acota al taller activo; endurecer
    // el filtro de crews sigue siendo de P9. Con cero filas la causa es la
    // política, no un desajuste de taller.
    await exigirFilas(
        supabase.from('crews').update(toSnake(updates)).eq('id', id).select('id'),
        'updateCrewDoc', `Crew ${id}`, 'updated',
    );
}
export async function deleteCrewDoc(id: string) {
    // 0-row guard surfaces an RLS-blocked delete that would otherwise look successful.
    // (Workspace-filter hardening for crews is owned by P9.)
    const { data, error } = await supabase.from('crews').delete().eq('id', id).select('id');
    if (error) { console.error('[deleteCrewDoc] Error:', error); throw new Error(error.message); }
    if (!data || data.length === 0) {
        throw new Error(`Crew ${id} not deleted (0 rows) — RLS policy blocked the delete.`);
    }
}

// ============================================================
//  SETTINGS
// ============================================================
export async function fetchBusinessSettings(wsId?: string) {
    const idToUse = wsId || getWorkspaceId();
    const { data, error } = await supabase.from('settings').select('*').eq('workspace_id', idToUse).maybeSingle();
    // A failed load (network, or maybeSingle() hitting >1 rows) must NOT be
    // mistaken for "no settings row exists" — that path lets the Setup Wizard
    // overwrite a tenant's real settings with defaults. Surface it so the
    // caller can hold an error state instead of silently defaulting.
    if (error) {
        console.error('[fetchBusinessSettings] Load error:', error);
        throw new Error(error.message);
    }
    return data ? toCamel(data) : null;
}
export async function saveBusinessSettings(data: any, wsId?: string) {
    // Pin the target workspace to the one the caller passes (SettingsContext
    // pins it at draft-load time). Reading localStorage here at save time let a
    // workspace switch in another tab write tenant A's settings into tenant B.
    const targetWs = wsId || getWorkspaceId();
    // Never persist settings against the 'default' fallback — it means the real
    // workspace never resolved (storage eviction / fetch error). Writing here
    // would create an orphan settings-default row and could let the Setup Wizard
    // finalize against the wrong workspace. Same guard family as requireWorkspaceId.
    if (targetWs === 'default') {
        throw new Error('Workspace still loading — please try again in a moment.');
    }
    console.log('[saveBusinessSettings] Saving to workspace:', targetWs);

    const payload = toSnake(data);
    // Remove any id from payload to avoid conflicts
    delete payload.id;

    // Single atomic upsert on the deterministic PK. The previous
    // SELECT-then-INSERT/UPDATE raced with itself on slow networks (a failed
    // existence check fell through to INSERT → 23505 on settings_pkey).
    // onConflict must be 'id' — it's the table's only unique constraint
    // (workspace_id is just an FK, using it would 42P10 on every save).
    const { data: saved, error } = await supabase.from('settings').upsert(
        { ...payload, id: `settings-${targetWs}`, workspace_id: targetWs },
        { onConflict: 'id' }
    ).select('id');
    if (error) {
        console.error('[saveBusinessSettings] Upsert error:', error);
        // PGRST204 = the payload names a column this database doesn't have. It
        // means a migration adding it hasn't run here, and the raw PostgREST
        // wording ("Could not find the 'x' column ... in the schema cache")
        // reads like a bug in the app to whoever gets the toast. Say what it
        // actually is — the whole settings save is refused, not just that field.
        //
        // `traducir(detectarIdioma(), …)` y no el hook: esto no es un componente.
        // detectarIdioma() lee localStorage en cada llamada, así que respeta el
        // idioma que la persona eligió — no el que había al cargar el módulo.
        if (error.code === 'PGRST204') {
            throw new Error(
                traducir(detectarIdioma(), 'ajustes.guardar.faltaColumna', { detalle: error.message }),
            );
        }
        throw error;
    }
    // Defensive: the write must have targeted the deterministic id. A mismatch
    // would mean a corrupted/duplicate settings row for this workspace — surface
    // it rather than silently leaving two rows that break the .maybeSingle() read.
    const savedId = saved?.[0]?.id;
    if (savedId && savedId !== `settings-${targetWs}`) {
        console.error('[saveBusinessSettings] Unexpected settings row id:', savedId);
        throw new Error(`Settings row id mismatch (${savedId} != settings-${targetWs})`);
    }
    console.log('[saveBusinessSettings] Saved successfully');
}
export async function getBusinessSettingsDoc(wsId?: string): Promise<any | null> {
    // Public proposal path: business settings are optional branding, so a load
    // error must never break the proposal page. (SettingsContext calls
    // fetchBusinessSettings directly and DOES surface load errors.)
    try {
        return await fetchBusinessSettings(wsId);
    } catch (err) {
        console.warn('[getBusinessSettingsDoc] settings load failed, continuing without:', err);
        return null;
    }
}

// ============================================================
//  SINGLE DOCUMENT READS
// ============================================================
/** Sin filtro de taller: `allowed_users` no tiene columna de taller y el
 *  alcance lo pone su RLS. `maybeSingle` por lo mismo que `getTimeLogDoc`. */
export async function getUserDoc(userId: string): Promise<any | null> {
    const { data, error } = await supabase.from('allowed_users').select('*').eq('id', userId).maybeSingle();
    if (error) { console.error('[getUserDoc] error:', error.message); throw new Error(error.message); }
    return data ? toCamel(data) : null;
}
export async function getClientDoc(wsId: string, clientId: string): Promise<Client | null> {
    const { data, error } = await supabase.from('clients').select('*').eq('id', clientId).eq('workspace_id', wsId).maybeSingle();
    if (error) { console.error('[getClientDoc] error:', error.message); throw new Error(error.message); }
    return data ? toCamel(data) : null;
}
